import { getDownloadURL, ref, uploadBytesResumable, type UploadTask } from '@firebase/storage';
import { type FirebaseError } from 'firebase/app';
import { type StorageError } from 'firebase/storage';

import { storage } from '@config/firebase';
import { format } from 'date-fns';
import { nlBE } from 'date-fns/locale';

export default async (
  file: File,
  {
    onStart,
    onProgress,
    onComplete,
    onFail,
    onCancel,
    storageRef,
    fileName,
    keepFileName,
  }: {
    onStart?: (uploadTasks: UploadTask) => void;
    onProgress?: (uploadTask: UploadTask, progress?: number) => void;
    onComplete: (imagePath: string, file: File, uploadtasks: UploadTask) => void;
    onFail?: (error: React.SetStateAction<null | FirebaseError | StorageError>) => void;
    onCancel?: (uploadTasks: UploadTask) => void;
    storageRef: string;
    fileName?: string;
    keepFileName?: boolean;
  },
) => {
  const dateOfUpload = format(new Date(), 'dd-MM-yyyy-T', { locale: nlBE });
  const name = fileName ?? file.name;
  // check if storageRef ends with a slash, if not add one
  const baseStoragePath = storageRef.endsWith('/') ? storageRef : storageRef + '/';
  const storagePath = keepFileName ? name : `${dateOfUpload}-${name}`;
  const fileStorageRef = ref(storage, baseStoragePath + storagePath);

  // console.log({ storagePath });
  const uploadTask = uploadBytesResumable(fileStorageRef, file);

  onStart && onStart(uploadTask);

  return new Promise((resolve, reject) => {
    uploadTask.on(
      'state_changed',
      snapshot => {
        const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
        onProgress && onProgress(uploadTask, progress);

        switch (snapshot.state) {
          case 'paused':
            console.log('Upload is paused');
            break;
          case 'running':
            // console.log("Upload is running");
            break;
          case 'canceled':
            reject(uploadTask);
            console.log('Upload is cancelled');
            break;
          case 'success':
            console.log('success from the switch', name);
            break;
          case 'error':
            reject(uploadTask);
            console.log('error from the switch', name);
            break;
        }
      },
      error => {
        switch (error.code) {
          case 'storage/unauthorized':
            console.error("User doesn't have permission to access the object");
            break;
          case 'storage/canceled':
            console.error('User canceled the upload');
            break;
          case 'storage/unknown':
            console.error('Unknown error occurred, inspect error.serverResponse');
            break;
          case 'storage/object-not-found':
            console.error('storage/object-not-found');
            break;
          case 'storage/bucket-not-found':
            console.error('storage/bucket-not-found');
            break;
          case 'storage/project-not-found':
            console.error('No project is configured for Cloud Storage');
            break;
          case 'storage/unauthenticated':
            console.error('User is unauthenticated, please authenticate and try again.');
            break;
          case 'storage/unauthorized':
            console.error(
              'User is not authorized to perform the desired action, check your security rules to ensure they are correct.',
            );
            break;
          case 'storage/object-not-found':
            console.error('storage/object-not-found');
            break;
          case 'storage/invalid-checksum':
            console.error(
              'File on the client does not match the checksum of the file received by the server. Try uploading again.',
            );
            break;
          case 'storage/unknown':
            console.error('Unknown error occurred, inspect error.serverResponse');
            break;
          case 'storage/invalid-event-name':
            console.error(
              'Invalid event name provided. Must be one of [`running`, `progress`, `pause`]',
            );
            break;
          case 'storage/invalid-url':
            console.error(
              'Invalid URL provided to refFromURL(). Must be of the form: gs://bucket/object or https://firebasestorage.googleapis.com/v0/b/bucket/o/object?token=<TOKEN>',
            );
            break;
          case 'storage/invalid-argument':
            console.error(
              'The argument passed to put() must be `File`, `Blob`, or `UInt8` Array. The argument passed to putString() must be a raw, `Base64`, or `Base64URL` string.',
            );
            break;
          case 'storage/no-default-bucket':
            console.error("No bucket has been set in your config's storageBucket property.");
            break;
          case 'storage/cannot-slice-blob':
            console.error(
              "Commonly occurs when the local file has changed (deleted, saved again, etc.). Try uploading again after verifying that the file hasn't changed.",
            );
            break;
          case 'storage/server-file-wrong-size':
            console.error(
              'File on the client does not match the size of the file recieved by the server. Try uploading again.',
            );
            break;
          default:
            reject(error);
            console.error('unknown error');
            break;
        }
        onFail && onFail(error);
        onCancel && onCancel(uploadTask);
      },
      () => {
        getDownloadURL(uploadTask.snapshot.ref).then(async () => {
          onComplete && onComplete(uploadTask.snapshot.ref.fullPath, file, uploadTask);
        });
        resolve(uploadTask);
      },
    );
  });
};
